import { useCallback, useEffect, useRef } from 'react';

/**
 * 작성 중 이탈 경고. 저장 안 된 변경이 있을 때(=dirty) 페이지를 벗어나려 하면 확인을 띄운다.
 *
 * 이 앱은 `<BrowserRouter>`(데이터 라우터 아님)라 react-router의 useBlocker를 못 쓴다.
 * 그래서 두 경로를 직접 잡는다:
 *  - 새로고침·탭 닫기·외부 이동 → `beforeunload`(브라우저 기본 경고문).
 *  - 앱 안 뒤로가기(하드웨어/브라우저 back, `navigate(-1)`인 '취소') → `popstate` 센티넬 + confirm.
 *
 * 센티넬: 마운트 시 히스토리 항목을 하나 쌓아, 첫 뒤로가기가 우리 핸들러에 걸리게 한다.
 *
 * 반환값 `safeLeave(done)`: 저장처럼 **의도된 이탈**을 할 때 호출한다. 쌓아둔 센티넬을
 * 접은 뒤 done()으로 이동하므로, 저장 직후 뒤로가기에서 빈 에디터가 남지 않는다.
 */
export function useLeaveConfirm(
  dirty: boolean,
  message: string,
): (done: () => void) => void {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const bypassRef = useRef(false); // 의도된 이탈 처리 중 — 가드를 잠깐 끈다.
  const armedRef = useRef(false); // 센티넬이 쌓여 있는가.

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (bypassRef.current || !dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    window.history.pushState(null, '', window.location.href); // 센티넬
    armedRef.current = true;

    const onPopState = () => {
      if (bypassRef.current) return; // safeLeave가 처리 중.
      if (!dirtyRef.current) {
        window.history.back();
        return;
      }
      if (window.confirm(message)) {
        window.removeEventListener('popstate', onPopState);
        armedRef.current = false;
        window.history.back();
      } else {
        window.history.pushState(null, '', window.location.href); // 센티넬 재장전
      }
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPopState);
    };
  }, [message]);

  return useCallback((done: () => void) => {
    bypassRef.current = true;
    if (!armedRef.current) {
      done();
      return;
    }
    // 센티넬(현재) → 에디터 진입 항목으로 한 칸 뒤로 간 뒤, 그 자리에서 replace 이동.
    // 결과 히스토리에 '빈 에디터' 항목이 남지 않는다.
    armedRef.current = false;
    const once = () => {
      window.removeEventListener('popstate', once);
      done();
    };
    window.addEventListener('popstate', once);
    window.history.back();
  }, []);
}
